// Inventory → "Add many parts": one table, the vendor once at the top, Enter for the next row,
// paste rows straight from Excel or pick an .xlsx / .csv. Part #s already in inventory are flagged, not added twice.
import { useMemo, useRef, useState } from 'react';
import { api, useApi, mayI, toCents, dollars, type Row } from '../lib';
import { priceFromCost, markupOf } from '../../shared/calc';
import { parseCsv, readSheet } from '../sheet';
import { Button, Icon, Modal, useToast, PickSelect } from '../ui';
import { VendorModal } from './Purchasing';

type R = { sku: string; name: string; bin: string; cost: string; price: string; qty: string; vendor: string; exists?: Row | null; err?: string; done?: boolean };
const COLS = ['sku', 'name', 'bin', 'cost', 'price', 'qty'] as const;
type Col = (typeof COLS)[number];
const blank = (): R => ({ sku: '', name: '', bin: '', cost: '', price: '', qty: '', vendor: '' });
const num = (s: string) => s.replace(/[^\d.]/g, '');

/** Which column of a spreadsheet is which, from the header names. */
function mapHeaders(h: string[]): (Col | 'vendor' | null)[] {
  return h.map(x => {
    const t = x.toLowerCase();
    if (/vendor|supplier/.test(t)) return 'vendor';
    if (/desc|name|item/.test(t) && !/part\s*(#|no|num)/.test(t)) return 'name';
    if (/part|sku|number|p\/n|pn\b|#/.test(t)) return 'sku';
    if (/bin|location|shelf loc/.test(t)) return 'bin';
    if (/cost|your price|net|unit price|each/.test(t)) return 'cost';
    if (/sell|price|retail|list/.test(t)) return 'price';
    if (/qty|quantity|stock|on hand|count|shelf/.test(t)) return 'qty';
    return null;
  });
}

export function AddManyParts({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const costs = mayI('costs');
  const vendors = useApi<Row[]>('/api/vendors');
  const settings = useApi<Row>('/api/settings');
  const [vendor, setVendor] = useState('');
  const [addingVendor, setAddingVendor] = useState<boolean | string>(false);
  const [rows, setRows] = useState<R[]>(() => Array.from({ length: 5 }, blank));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const names = useMemo(() => [...new Set([...(vendors.data || []).filter(v => v.active !== 0).map(v => String(v.name)), ...rows.map(r => r.vendor).filter(Boolean), ...(vendor ? [vendor] : [])])].sort((a, b) => a.localeCompare(b)), [vendors.data, rows, vendor]);
  const suggested = (r: R) => (r.cost ? priceFromCost(toCents(r.cost), settings.data || {}) : 0);
  const filled = rows.filter(r => r.sku.trim() || r.name.trim());
  const toSave = filled.filter(r => !r.exists && !r.done);

  const set = (i: number, k: keyof R, val: string) => setRows(x => x.map((r, j) => (j === i ? { ...r, [k]: val, ...(k === 'sku' ? { exists: undefined, err: undefined } : {}) } : r)));
  const focus = (i: number, col: string) => setTimeout(() => table.current?.querySelector<HTMLInputElement>(`[data-cell="${i}-${col}"]`)?.focus(), 30);
  const check = async (i: number, sku: string) => {
    if (!sku.trim()) return;
    try {
      const r = await api<{ part: Row | null }>(`/api/parts/lookup?sku=${encodeURIComponent(sku.trim())}`);
      setRows(x => x.map((row, j) => (j === i && row.sku === sku ? { ...row, exists: r.part } : row)));
    } catch { /* the save checks too */ }
  };
  // Rows pasted from Excel (tab-separated) fill this row and the ones below, starting at this column.
  const paste = (i: number, col: Col, e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!/[\t\n]/.test(text.trim())) return;
    e.preventDefault();
    const grid = parseCsv(text.replace(/\r?\n$/, '')).filter(r => r.some(c => c.trim()));
    const start = COLS.indexOf(col);
    setRows(x => {
      const out = [...x];
      grid.forEach((cells, k) => {
        const r = { ...(out[i + k] || blank()) };
        cells.forEach((cell, c) => { const key = COLS[start + c]; if (key) r[key] = ['cost', 'price', 'qty'].includes(key) ? num(cell) : cell.trim(); });
        r.exists = undefined;
        out[i + k] = r;
      });
      if (!out.some(r => !r.sku && !r.name)) out.push(blank());
      return out;
    });
    grid.forEach((cells, k) => { if (start === 0 && cells[0]) void check(i + k, cells[0].trim()); });
    toast(`${grid.length} row${grid.length > 1 ? 's' : ''} pasted`);
  };
  async function fromFile(f: File) {
    try {
      const sh = await readSheet(f);
      const map = mapHeaders(sh.headers);
      const positional = !map.some(m => m === 'sku');
      const got: R[] = sh.rows.map(cells => {
        const r = blank();
        cells.forEach((cell, c) => {
          const key = positional ? COLS[c] : map[c];
          if (!key) return;
          r[key] = ['cost', 'price', 'qty'].includes(key) ? num(cell) : cell.trim();
        });
        if (r.vendor === vendor) r.vendor = '';
        return r;
      }).filter(r => r.sku || r.name).slice(0, 300);
      if (!got.length) return toast('No parts found in that file', 'err');
      const base = rows.filter(r => r.sku || r.name).length;
      setRows(x => [...x.filter(r => r.sku || r.name), ...got, blank()]);
      got.forEach((r, k) => r.sku && void check(base + k, r.sku));
      toast(`${got.length} parts from ${f.name} — check them, then Save`);
    } catch (e) { toast((e as Error).message, 'err'); }
  }
  const onKey = (i: number, col: Col, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (i === rows.length - 1) setRows(x => [...x, blank()]);
    focus(i + 1, 'sku');
    void col;
  };
  async function save() {
    const bad = toSave.find(r => !r.sku.trim() || !r.name.trim());
    if (bad) return toast(`Every row needs a part # and a description${bad.sku ? ` (${bad.sku})` : ''}`, 'err');
    if (!toSave.length) return toast(filled.length ? 'Those parts are all in inventory already' : 'Type a part # first', 'err');
    setBusy(true);
    let ok = 0;
    const next = [...rows];
    for (let i = 0; i < next.length; i++) {
      const r = next[i];
      if (!(r.sku.trim() || r.name.trim()) || r.exists || r.done) continue;
      try {
        await api('/api/parts', { body: {
          sku: r.sku.trim(), name: r.name.trim(), bin: r.bin.trim(), supplier: (r.vendor || vendor).trim(), min_stock: 0, stock: Math.max(0, Math.round(Number(r.qty) || 0)),
          cost: costs ? toCents(r.cost) : 0, price: r.price ? toCents(r.price) : suggested(r), taxable: true,
        } });
        next[i] = { ...r, done: true, err: undefined }; ok++;
      } catch (e) { next[i] = { ...r, err: (e as Error).message }; }
      setRows([...next]);
    }
    setBusy(false);
    const failed = next.filter(r => r.err).length;
    if (ok) toast(`${ok} part${ok > 1 ? 's' : ''} added${failed ? ` — ${failed} not added, see the red rows` : ''}`, failed ? 'err' : undefined);
    if (!failed) onDone();
  }

  return (
    <Modal title="Add many parts" onClose={onClose} wide>
      <div className="form"><div className="form-body">
        <label className="field"><span>Vendor for all parts</span>
          <PickSelect value={vendor} ariaLabel="Vendor for all parts" none="— none —" options={names.map(n => ({ value: n, label: n }))} onChange={setVendor}
            onAdd={t => setAddingVendor(t || true)} addLabel="+ Add vendor…" /><small>Every row gets this vendor. A part from somewhere else — change the vendor on that row.</small></label>
        <div className="field am-file"><span>Have a list?</span>
          <div className="actions-row"><Button icon="upload" onClick={() => fileRef.current?.click()}>Excel / CSV file</Button></div>
          <small>Or copy the rows in Excel and paste them into the first Part # box.</small>
          <input ref={fileRef} type="file" hidden accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={e => { const f = e.target.files?.[0]; if (f) void fromFile(f); e.target.value = ''; }} /></div>
        <div className="full table-wrap">
          <table className="po-lines am-table" ref={table}><thead><tr><th>#</th><th>Part #</th><th>Description</th><th>Bin</th>{costs && <th>Cost each</th>}<th>Sell price</th><th>Qty on shelf</th><th>Vendor</th><th /></tr></thead>
            <tbody>{rows.map((r, i) => {
              const sug = suggested(r);
              const mk = markupOf(toCents(r.cost), r.price ? toCents(r.price) : sug);
              return (
                <tr key={i} className={r.done ? 'am-done' : r.err ? 'am-err' : r.exists ? 'am-exists' : ''}>
                  <td className="muted">{r.done ? '✓' : i + 1}</td>
                  <td><input data-cell={`${i}-sku`} value={r.sku} disabled={r.done} onChange={e => set(i, 'sku', e.target.value)} onBlur={e => void check(i, e.target.value)} onPaste={e => paste(i, 'sku', e)} onKeyDown={e => onKey(i, 'sku', e)} placeholder="Part #" aria-label={`Part # row ${i + 1}`} />
                    {r.exists && <small className="warn-text">Already in inventory{r.exists.name ? ` as "${r.exists.name}"` : ''} — skipped (use Receive to add stock)</small>}
                    {r.err && <small className="danger">{r.err}</small>}</td>
                  <td><input data-cell={`${i}-name`} value={r.name} disabled={r.done} onChange={e => set(i, 'name', e.target.value)} onPaste={e => paste(i, 'name', e)} onKeyDown={e => onKey(i, 'name', e)} placeholder="Description" aria-label={`Description row ${i + 1}`} /></td>
                  <td><input value={r.bin} disabled={r.done} onChange={e => set(i, 'bin', e.target.value)} onPaste={e => paste(i, 'bin', e)} onKeyDown={e => onKey(i, 'bin', e)} className="am-bin" placeholder="Bin" aria-label={`Bin row ${i + 1}`} /></td>
                  {costs && <td><input value={r.cost} disabled={r.done} onChange={e => set(i, 'cost', num(e.target.value))} onPaste={e => paste(i, 'cost', e)} onKeyDown={e => onKey(i, 'cost', e)} inputMode="decimal" className="num" placeholder="0.00" aria-label={`Cost row ${i + 1}`} /></td>}
                  <td><input value={r.price} disabled={r.done} onChange={e => set(i, 'price', num(e.target.value))} onPaste={e => paste(i, 'price', e)} onKeyDown={e => onKey(i, 'price', e)} inputMode="decimal" className="num" placeholder={sug ? (sug / 100).toFixed(2) : '0.00'} aria-label={`Price row ${i + 1}`} />
                    {costs && mk !== null && <small className={'mk-mini' + (mk < 0 ? ' danger' : '')}>{mk >= 0 ? '+' : ''}{mk}%{!r.price && sug ? ' auto' : ''}</small>}</td>
                  <td><input value={r.qty} disabled={r.done} onChange={e => set(i, 'qty', e.target.value.replace(/\D/g, ''))} onPaste={e => paste(i, 'qty', e)} onKeyDown={e => onKey(i, 'qty', e)} inputMode="numeric" className="num qty" placeholder="0" aria-label={`Quantity row ${i + 1}`} /></td>
                  <td className="po-vendor"><PickSelect value={r.vendor} disabled={r.done} className={r.vendor && r.vendor !== vendor ? 'own' : ''} onChange={x => set(i, 'vendor', x)} ariaLabel={`Vendor row ${i + 1}`}
                    none={vendor ? `Same — ${vendor}` : 'Same as top'} options={names.filter(n => n !== vendor).map(n => ({ value: n, label: n }))} onAdd={t => { if (t) set(i, 'vendor', t); else setAddingVendor(true); }} addLabel="+ Add vendor…" /></td>
                  <td>{!r.done && <button type="button" className="icon-btn" title="Remove row" onClick={() => setRows(x => x.length > 1 ? x.filter((_, j) => j !== i) : [blank()])}><Icon name="x" size={14} /></button>}</td>
                </tr>);
            })}</tbody></table>
          <button type="button" className="linkish" onClick={() => { setRows(x => [...x, blank()]); focus(rows.length, 'sku'); }}>+ Add row</button>
        </div>
        <p className="form-note full">Press <b>Enter</b> to jump to the next row. Sell price left empty = your markup from Settings{costs ? ' (the % shows under it)' : ''}. Qty on shelf is what's there now (opening stock).</p>
      </div>
        <div className="form-foot"><Button onClick={onClose}>Cancel</Button><span className="spacer" />
          {costs && toSave.length > 0 && <small className="muted">Stock value {dollars(toSave.reduce((a, r) => a + toCents(r.cost) * (Number(r.qty) || 0), 0))}</small>}
          <Button kind="primary" icon="check" busy={busy} disabled={!toSave.length} onClick={() => void save()}>Save {toSave.length || ''} part{toSave.length === 1 ? '' : 's'}</Button></div>
      </div>
      {addingVendor && <VendorModal name={typeof addingVendor === 'string' ? addingVendor : undefined} onClose={() => setAddingVendor(false)} onSaved={n => { setAddingVendor(false); vendors.reload(); if (n) setVendor(n); }} />}
    </Modal>
  );
}
